import { t } from '../../../../../services/texts.service';
import ValidationInfo from '../../../../../validation/validation-info';
import CheckboxFormGroup from '../../../../Shared/CheckboxFormGroup';
import { Icon } from '../../../../Shared/Icon';
import { IconColor } from '../../../../Shared/Icon/icon-color.enum';
import { IconMotif } from '../../../../Shared/Icon/icon-motif.enum';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { getDefinedStopAreas } from '../../helpers/settings.helper';
import { ISettings, StopAreaSlots } from '../../models/settings.model';
import { StopAreaType } from '../../models/stop-area-type.enum';
import SelectStopAreas from './components/SelectStopAreas';
import TransportModeFilter from '../../../../Shared/TransportModeFilter';
import { TransportMode } from '../../../../../models/transport-mode.enum';
import { TransportSubMode } from '../../../../../models/transport-sub-mode.enum';

export default function Settings({ mySettings, className, onChange, onClose }: { mySettings: ISettings; className?: string; onChange: (settings: ISettings) => void; onClose: () => void }) {
  // === VARIABLES ===
  const selectedOptions = [];
  if (mySettings.enableAutoRefresh) selectedOptions.push('enableAutoRefresh');
  if (mySettings.showTrips) selectedOptions.push('showTrips');
  if (mySettings.showCommuterParkingLots) selectedOptions.push('showCommuterParkingLots');
  if (mySettings.showTrafficInformation) selectedOptions.push('showTrafficInformation');
  if (mySettings.filterTrafficSituationsByTrips) selectedOptions.push('filterTrafficSituationsByTrips');
  if (mySettings.showOnlyFastestTripPerDeparture) selectedOptions.push('showOnlyFastestTripPerDeparture');
  if (mySettings.showLeavesIn) selectedOptions.push('showLeavesIn');
  if (mySettings.showDepartureTime) selectedOptions.push('showDepartureTime');
  if (mySettings.showOriginStopName) selectedOptions.push('showOriginStopName');
  if (mySettings.showOnlyFirstBadgeInSummary) selectedOptions.push('showOnlyFirstBadgeInSummary');

  // === EVENT HANDLERS ===
  function handleSelectHomeStopAreasSave(stopAreas: StopAreaSlots): void {
    handleSelectStopAreasSave(StopAreaType.Home, stopAreas);
  }

  function handleSelectWorkStopAreasSave(stopAreas: StopAreaSlots): void {
    handleSelectStopAreasSave(StopAreaType.Work, stopAreas);
  }

  function handleSelectStopAreasSave(type: StopAreaType, stopAreas: StopAreaSlots): void {
    if (type === StopAreaType.Home) {
      const selectedHomeStopAreaCount = getDefinedStopAreas(stopAreas).length.toString();
      AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Sparat hemhållplatser', `Antal sparade hemhållplatser: ${selectedHomeStopAreaCount}`);
      onChange({ ...mySettings, homeStopAreas: stopAreas });
    } else if (type === StopAreaType.Work) {
      const selectedWorkStopAreaCount = getDefinedStopAreas(stopAreas).length.toString();
      AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Sparat arbetshållplatser', `Antal sparade arbetshållplatser: ${selectedWorkStopAreaCount}`);
      onChange({ ...mySettings, workStopAreas: stopAreas });
    }
  }

  function handleCommonSettingsChange(value: string[]): void {
    const newEnableAutoRefreshValue = value.includes('enableAutoRefresh');
    if (newEnableAutoRefreshValue !== !!mySettings.enableAutoRefresh) AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Uppdatera automatiskt', newEnableAutoRefreshValue ? 'Ja' : 'Nej');
    onChange({
      ...mySettings,
      enableAutoRefresh: newEnableAutoRefreshValue,
    });
  }

  function handleTripsSettingsChange(value: string[]): void {
    const trackIfChanged = (key: keyof ISettings, action: string) => {
      const newValue = value.includes(key as string);
      if (newValue === !!mySettings[key]) return;
      AnalyticsService.addEvent(AnalyticsService.MyStopAreas, action, newValue ? 'Aktiverade' : 'Inaktiverade');
    };
    trackIfChanged('showTrips', 'Visa resor');
    trackIfChanged('showOnlyFastestTripPerDeparture', 'Visa endast snabbaste resan för varje avgång');
    trackIfChanged('showLeavesIn', 'Visa antal minuter tills resan avgår');
    trackIfChanged('showDepartureTime', 'Visa avgångstid');
    trackIfChanged('showOriginStopName', 'Visa avgångshållplats när flera finns');
    trackIfChanged('showOnlyFirstBadgeInSummary', 'Visa bara avgångslinjen i översikten');

    onChange({
      ...mySettings,
      showTrips: value.includes('showTrips'),
      showOnlyFastestTripPerDeparture: value.includes('showOnlyFastestTripPerDeparture'),
      showLeavesIn: value.includes('showLeavesIn'),
      showDepartureTime: value.includes('showDepartureTime'),
      showOriginStopName: value.includes('showOriginStopName'),
      showOnlyFirstBadgeInSummary: value.includes('showOnlyFirstBadgeInSummary'),
    });
  }

  function handleTrafficInformationSettingsChange(value: string[]): void {
    const newValue = value.includes('showTrafficInformation');
    if (newValue !== !!mySettings.showTrafficInformation) AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Visa trafikinformation', newValue ? 'Ja' : 'Nej');
    const newFilterValue = value.includes('filterTrafficSituationsByTrips');
    if (newFilterValue !== !!mySettings.filterTrafficSituationsByTrips)
      AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Visa bara störningar för linjer i mina resor', newFilterValue ? 'Aktiverade' : 'Inaktiverade');
    onChange({
      ...mySettings,
      showTrafficInformation: newValue,
      filterTrafficSituationsByTrips: newFilterValue,
    });
  }

  function handleCommuterParkingLotsSettingsChange(value: string[]): void {
    const newValue = value.includes('showCommuterParkingLots');
    if (newValue !== !!mySettings.showCommuterParkingLots) AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Visa lediga pendelparkeringar', newValue ? 'Ja' : 'Nej');
    onChange({
      ...mySettings,
      showCommuterParkingLots: newValue,
    });
  }

  // === RENDERING ===
  return (
    <div className={`d-flex flex-column gap-4 mb-4 ${className ?? ''}`.trimEnd()}>
      <div>
        <button className="btn btn-tertiary d-flex align-items-center" onClick={onClose}>
          <Icon className="pe-1" motif={IconMotif.ArrowLeft} color={IconColor.Blue1000} altText=""></Icon>
          <span>{t('Tillbaka till hållplatser', 'Back to stop areas')}</span>
        </button>
      </div>
      <div>
        <h2>{t('Inställningar', 'Settings')}</h2>
        <p className="mb-0">{t('Anpassa din översikt här. Dina inställningar sparas automatiskt.', 'Customize your overview here. Your settings are saved automatically.')}</p>
      </div>
      <div>
        <h3>{t('Hållplatser', 'Stop areas')}</h3>
        <SelectStopAreas type={StopAreaType.Home} stopAreas={mySettings.homeStopAreas} className="mb-3" onSave={handleSelectHomeStopAreasSave} />
        <SelectStopAreas type={StopAreaType.Work} stopAreas={mySettings.workStopAreas} className="mb-0" onSave={handleSelectWorkStopAreasSave} />
      </div>
      <div>
        <h3>{t('Visningsalternativ', 'Display options')}</h3>
        <div className="border rounded p-3 bg-white">
          <h4>{t('Allmänt', 'General')}</h4>
          <CheckboxFormGroup
            options={[{ value: 'enableAutoRefresh', text: t('Uppdatera automatiskt', 'Enable auto refresh') }]}
            state={{
              value: selectedOptions,
              validationInfo: new ValidationInfo(),
            }}
            onChange={handleCommonSettingsChange}
            className="mb-3"
          ></CheckboxFormGroup>

          <h4>{t('Resor', 'Trips')}</h4>
          <CheckboxFormGroup
            options={[
              { value: 'showTrips', text: t('Visa resor', 'Show trips') },
              { value: 'showOnlyFastestTripPerDeparture', text: t('Visa endast snabbaste resan för varje avgång', 'Show only the fastest trip for each departure') },
              { value: 'showLeavesIn', text: t('Visa antal minuter tills resan avgår', 'Show how many minutes until the trip leaves') },
              { value: 'showDepartureTime', text: t('Visa avgångstid', 'Show departure time') },
              { value: 'showOriginStopName', text: t('Visa avgångshållplats när flera finns', 'Show departure stop when multiple exist') },
              { value: 'showOnlyFirstBadgeInSummary', text: t('Visa bara avgångslinjen i översikten', 'Show only the departure line in the overview') },
            ]}
            state={{
              value: selectedOptions,
              validationInfo: new ValidationInfo(),
            }}
            onChange={handleTripsSettingsChange}
            className="mb-3"
          ></CheckboxFormGroup>

          <fieldset className="mb-3">
            <legend className="h4">{t('Trafikslag för resor', 'Modes of transport for trips')}</legend>
            <TransportModeFilter
              availableTransportModes={[TransportMode.tram, TransportMode.bus, TransportMode.ferry]}
              availableTransportSubModes={[TransportSubMode.vasttagen, TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain]}
              groupNonVasttagenAsOtherTrains={true}
              selectedTransportModes={mySettings.transportModes}
              selectedTransportSubModes={mySettings.transportSubModes}
              onChange={(transportModes, transportSubModes) => {
                onChange({ ...mySettings, transportModes, transportSubModes });
                AnalyticsService.addEvent(AnalyticsService.MyStopAreas, `Trafikslag inställningar`, 'Klick');
              }}
            />
          </fieldset>

          <h4>{t('Trafikinformation', 'Traffic information')}</h4>
          <CheckboxFormGroup
            options={[
              { value: 'showTrafficInformation', text: t('Visa trafikinformation', 'Show traffic information') },
              { value: 'filterTrafficSituationsByTrips', text: t('Visa bara störningar för linjer i mina resor', 'Show only traffic situations for lines in my trips') },
            ]}
            state={{
              value: selectedOptions,
              validationInfo: new ValidationInfo(),
            }}
            onChange={handleTrafficInformationSettingsChange}
            className="mb-3"
          ></CheckboxFormGroup>

          <h4>{t('Pendelparkeringar', 'Commuter parking lots')}</h4>
          <CheckboxFormGroup
            options={[{ value: 'showCommuterParkingLots', text: t('Visa lediga pendelparkeringar', 'Show free commuter parking lots') }]}
            state={{
              value: selectedOptions,
              validationInfo: new ValidationInfo(),
            }}
            onChange={handleCommuterParkingLotsSettingsChange}
            className="mb-0"
          ></CheckboxFormGroup>
        </div>
      </div>
    </div>
  );
}
